import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, Card, Group, Stack, Table, Title } from "@mantine/core";

import {
  ADMIN_ORGANIZATION,
  SONOMETER_CREATE_AUTHORISED_ORGANIZATIONS,
  SONOMETER_CREATE_AUTHORISED_ROLES,
} from "_roles";
import type { Sonometer } from "sonometer/types/sonometer.model";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";

import {
  useListSonometersQuery,
  useRemoveSonometerMutation,
} from "sonometer/api/sonometer.api";

import { SonometerAdminRow } from "../components/SonometerAdminRow";
import { SonometerBreadcrumbs } from "../components/SonometerBreadcrumbs";

type SortedKeys = keyof Pick<
  Sonometer,
  "opponentName" | "championshipId" | "createdAt"
>;

function sortData(
  data: Sonometer[],
  payload: { sortBy: SortedKeys; reversed: boolean },
): Sonometer[] {
  return [...data].sort((a, b) => {
    const aValue = String(a[payload.sortBy]);
    const bValue = String(b[payload.sortBy]);
    return payload.reversed
      ? bValue.localeCompare(aValue)
      : aValue.localeCompare(bValue);
  });
}

export function SonometersListAdminView() {
  const navigate = useNavigate();
  const listSonometersQuery = useListSonometersQuery();
  const [sortedData, setSortedData] = useState<Sonometer[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("createdAt");
  const [reverseSortDirection, setReverseSortDirection] = useState(true);
  const [organization] = useOrganization();
  const [deleteSonometer] = useRemoveSonometerMutation();

  const sonometers = useMemo(
    () => listSonometersQuery.data,
    [listSonometersQuery.data],
  );

  const filteredSonometers = useMemo(
    () =>
      (sonometers || []).filter(
        (sonometer) => sonometer.createdAt && sonometer.opponentName,
      ),
    [sonometers],
  );

  const handleDelete = async (id: string) => {
    try {
      await deleteSonometer(id).unwrap();
      console.log(`Sonometer ${id} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete Sonometer", error);
    }
  };

  useEffect(() => {
    if (filteredSonometers) {
      const sorted = sortData(filteredSonometers, {
        sortBy,
        reversed: reverseSortDirection,
      });

      setSortedData(sorted);
    }
  }, [filteredSonometers, sortBy, reverseSortDirection]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align="center" m="xs">
          Sonometers
        </Title>
        <SonometerBreadcrumbs />

        <AccessRights
          authorizedOrganizations={SONOMETER_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={SONOMETER_CREATE_AUTHORISED_ROLES}
        >
          <Group position="right">
            <Button className="bg-button-admin" component={Link} to="new">
              New
            </Button>
          </Group>
        </AccessRights>
      </div>

      <Card shadow="sm" p="xl" className="overflow-visible">
        <QueryWrapper query={listSonometersQuery}>
          {listSonometersQuery.isError ? (
            <Stack align="center">
              <Box color="red">Error loading sonometers: API returned 404</Box>
              <Box>
                Please check if you are authenticated and have proper
                permissions
              </Box>
            </Stack>
          ) : filteredSonometers && filteredSonometers.length > 0 ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "opponentName"}
                    onClick={() =>
                      setSortBy((prev) => {
                        setReverseSortDirection(
                          prev === "opponentName"
                            ? !reverseSortDirection
                            : false,
                        );
                        return "opponentName";
                      })
                    }
                  >
                    Opponent Name
                  </Th>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "championshipId"}
                    onClick={() =>
                      setSortBy((prev) => {
                        setReverseSortDirection(
                          prev === "championshipId"
                            ? !reverseSortDirection
                            : false,
                        );
                        return "championshipId";
                      })
                    }
                  >
                    Championship ID
                  </Th>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "createdAt"}
                    onClick={() =>
                      setSortBy((prev) => {
                        setReverseSortDirection(
                          prev === "createdAt" ? !reverseSortDirection : false,
                        );
                        return "createdAt";
                      })
                    }
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((sonometer) =>
                  sonometer.owner.L1 === organization ||
                  organization === ADMIN_ORGANIZATION ? (
                    <SonometerAdminRow
                      key={sonometer.id}
                      sonometer={sonometer}
                      onEdit={() => navigate(sonometer.id)}
                      onDelete={() => handleDelete(sonometer.id)}
                      onDuplicate={() => navigate(`${sonometer.id}/duplicate`)}
                    />
                  ) : null,
                )}
              </tbody>
            </Table>
          ) : (
            <Stack align="center">
              <Box>No sonometers found or API not available</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                New
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
